import type {
	DependencyHealthDetails,
	DependencyHealthReporter,
	DependencyOutcome,
} from '../dependencyHealthAnalytics.ts';
import {
	createDependencyHealthObservation,
	dependencyHealthDuration,
	dependencyHealthTime,
} from '../utils/dependencyHealthObservation.ts';
import {
	classifyLinkedJiraWorkItemsError,
	isLinkedJiraWorkItemsClientError,
	type LinkedJiraAggregateObservation,
} from '../utils/fetchLinkedJiraWorkItemUrls.ts';

import {
	getIncomingLinksRemoteLinksFailedEvent,
	getIncomingLinksRemoteLinksSkippedEvent,
	type RemoteLinksTimings,
} from './incomingLinksAnalytics.ts';

type RemoteLinksEvent =
	| ReturnType<typeof getIncomingLinksRemoteLinksFailedEvent>
	| ReturnType<typeof getIncomingLinksRemoteLinksSkippedEvent>;

export type RemoteLinksEventFactory = (event: RemoteLinksEvent) => { fire: () => void };

export type RemoteLinksObservation = {
	aggregateSettled: (result: LinkedJiraAggregateObservation, timedOut: boolean) => void;
	requestStarted: () => void;
	requestFinished: () => void;
	hydrationStarted: (count: number) => void;
	hydrationFinished: () => void;
	failed: (error: unknown, timedOut: boolean) => void;
	skipped: (incomplete: boolean) => void;
	cancel: () => void;
	timings: () => RemoteLinksTimings;
};

const aggregateOutcome = (
	result: LinkedJiraAggregateObservation,
	timedOut: boolean,
): DependencyOutcome => {
	if (timedOut) {
		return 'timeout';
	}
	if (result.error instanceof Error && result.error.name === 'AbortError') {
		return 'cancelled';
	}
	if (isLinkedJiraWorkItemsClientError(result.error)) {
		return 'denied';
	}
	if (result.outcome === 'failed' || result.outcome === 'unavailable') {
		return result.outcome;
	}
	if (result.incomplete) {
		return 'partial';
	}
	return result.outcome === 'empty' ? 'empty' : 'succeeded';
};

const aggregateErrorType = (
	result: LinkedJiraAggregateObservation,
	timedOut: boolean,
): DependencyHealthDetails => {
	if (result.outcome !== 'failed' || timedOut) {
		return {};
	}
	const type = classifyLinkedJiraWorkItemsError(result.error, false);
	if (type === 'badStatus') {
		return { errorType: isLinkedJiraWorkItemsClientError(result.error) ? 'client' : 'server' };
	}
	if (type === 'parse') {
		return { errorType: 'parse' };
	}
	return { errorType: 'network' };
};

export const createRemoteLinksObservation = (
	createEvent: RemoteLinksEventFactory,
	reportHealth: DependencyHealthReporter,
): RemoteLinksObservation => {
	const aggregate = createDependencyHealthObservation(reportHealth, {
		dependency: 'jiraMetadataAggregate',
		operation: 'read',
	});
	const timings: RemoteLinksTimings = {};
	let requestStartedAt: number | undefined;
	let hydrationStartedAt: number | undefined;
	let stage: 'query' | 'hydration' = 'query';
	const fire = (event: RemoteLinksEvent): void => {
		try {
			createEvent(event).fire();
		} catch {
			// The best-effort fallback must settle even if analytics cannot dispatch.
		}
	};
	return {
		aggregateSettled: (result, timedOut) => {
			timings.aggregateDurationMs = aggregate.finish(
				aggregateOutcome(result, timedOut),
				aggregateErrorType(result, timedOut),
			)?.durationMs;
		},
		requestStarted: () => {
			requestStartedAt = dependencyHealthTime();
		},
		requestFinished: () => {
			timings.requestDurationMs = dependencyHealthDuration(requestStartedAt);
		},
		hydrationStarted: (count) => {
			stage = 'hydration';
			if (count > 0) {
				hydrationStartedAt = dependencyHealthTime();
			}
		},
		hydrationFinished: () => {
			timings.hydrationDurationMs = dependencyHealthDuration(hydrationStartedAt);
		},
		failed: (error, timedOut) => {
			const startedAt = stage === 'query' ? requestStartedAt : hydrationStartedAt;
			fire(
				getIncomingLinksRemoteLinksFailedEvent({
					errorType: classifyLinkedJiraWorkItemsError(error, timedOut),
					expected: isLinkedJiraWorkItemsClientError(error) || timedOut,
					stage,
					durationMs: dependencyHealthDuration(startedAt),
					aggregateDurationMs: timings.aggregateDurationMs,
				}),
			);
		},
		skipped: (incomplete) => {
			fire(
				getIncomingLinksRemoteLinksSkippedEvent({
					incomplete,
					aggregateDurationMs: timings.aggregateDurationMs,
				}),
			);
		},
		cancel: () => {
			aggregate.finish('cancelled');
		},
		timings: () => ({ ...timings }),
	};
};
